import { useState, useEffect } from "react";
import { useAuth, useLanguage, API } from "../App";
import { AppLayout } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Plus,
  Search,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowRight
} from "lucide-react";
import { toast } from "sonner";

export const ChangeOrdersPage = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [changeOrders, setChangeOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState("");
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    estimated_cost: 0,
    estimated_hours: 0,
    material_cost: 0,
    subcontractor_cost: 0
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchChangeOrders(selectedProject);
    } else if (projects.length > 0) {
      fetchAllChangeOrders();
    }
  }, [selectedProject, projects]);

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setProjects(data);
      }
    } catch (err) {
      console.error("Failed to fetch projects:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllChangeOrders = async () => {
    if (projects.length === 0) {
      setChangeOrders([]);
      return;
    }
    setLoading(true);
    try {
      const allCOs = [];
      for (const project of projects) {
        const res = await fetch(`${API}/projects/${project.project_id}/change-orders`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include"
        });
        if (res.ok) {
          const data = await res.json();
          allCOs.push(...data);
        }
      }
      setChangeOrders(allCOs);
    } catch (err) {
      console.error("Failed to fetch change orders:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchChangeOrders = async (projectId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/change-orders`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        setChangeOrders(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch change orders:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProject) {
      toast.error(language === "sv" ? "Välj ett projekt" : "Select a project");
      return;
    }
    
    try {
      const res = await fetch(`${API}/projects/${selectedProject}/change-orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success(language === "sv" ? "ÄTA skapad!" : "Change order created!");
        setDialogOpen(false);
        setFormData({ title: "", description: "", estimated_cost: 0, estimated_hours: 0, material_cost: 0, subcontractor_cost: 0 });
        fetchChangeOrders(selectedProject);
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    }
  };

  const updateStatus = async (coId, newStatus) => {
    try {
      const res = await fetch(`${API}/change-orders/${coId}/status?status=${newStatus}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      
      if (res.ok) {
        toast.success(language === "sv" ? "Status uppdaterad!" : "Status updated!");
        if (selectedProject) {
          fetchChangeOrders(selectedProject);
        } else {
          fetchAllChangeOrders();
        }
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    }
  };

  const statusConfig = {
    draft: { color: "bg-slate-100 text-slate-800", icon: FileText, label: language === "sv" ? "Utkast" : "Draft" },
    submitted: { color: "bg-blue-100 text-blue-800", icon: Clock, label: language === "sv" ? "Inskickad" : "Submitted" },
    under_review: { color: "bg-amber-100 text-amber-800", icon: AlertCircle, label: language === "sv" ? "Under granskning" : "Under Review" },
    approved: { color: "bg-green-100 text-green-800", icon: CheckCircle, label: language === "sv" ? "Godkänd" : "Approved" },
    rejected: { color: "bg-red-100 text-red-800", icon: XCircle, label: language === "sv" ? "Avvisad" : "Rejected" },
    completed: { color: "bg-purple-100 text-purple-800", icon: CheckCircle, label: language === "sv" ? "Slutförd" : "Completed" }
  };

  const filteredCOs = changeOrders.filter(co =>
    co.title.toLowerCase().includes(search.toLowerCase()) ||
    co.description.toLowerCase().includes(search.toLowerCase())
  );

  const totalValue = filteredCOs.reduce((sum, co) => 
    sum + co.estimated_cost + co.material_cost + co.subcontractor_cost, 0
  );

  const pendingCount = filteredCOs.filter(co => 
    ["draft", "submitted", "under_review"].includes(co.status)
  ).length;

  return (
    <AppLayout>
      <div className="p-4 lg:p-6 space-y-6 pb-20 lg:pb-6" data-testid="change-orders-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
              {language === "sv" ? "ÄTA-hantering" : "Change Orders"}
            </h1>
            <p className="text-slate-600 mt-1">
              {language === "sv" 
                ? "Hantera ändrings- och tilläggsarbeten" 
                : "Manage change and additional work"}
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-touch" data-testid="create-co-btn">
                <Plus className="w-5 h-5 mr-2" />
                {language === "sv" ? "Ny ÄTA" : "New Change Order"}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
                  {language === "sv" ? "Skapa ny ÄTA" : "Create Change Order"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="form-label">{language === "sv" ? "Projekt" : "Project"} *</Label>
                  <Select value={selectedProject} onValueChange={setSelectedProject}>
                    <SelectTrigger data-testid="co-project-select">
                      <SelectValue placeholder={language === "sv" ? "Välj projekt" : "Select project"} />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map(p => (
                        <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="form-label">{language === "sv" ? "Titel" : "Title"} *</Label>
                  <Input 
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder={language === "sv" ? "T.ex. Tillägg el-uttag" : "E.g. Additional outlets"}
                    required
                    data-testid="co-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="form-label">{language === "sv" ? "Beskrivning" : "Description"} *</Label>
                  <Textarea 
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder={language === "sv" ? "Detaljerad beskrivning av arbetet..." : "Detailed description of the work..."}
                    rows={3}
                    required
                    data-testid="co-desc-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="form-label">{language === "sv" ? "Arbetstid (h)" : "Work hours"}</Label>
                    <Input 
                      type="number"
                      value={formData.estimated_hours}
                      onChange={(e) => setFormData({...formData, estimated_hours: parseFloat(e.target.value) || 0})}
                      data-testid="co-hours-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="form-label">{language === "sv" ? "Arbetskostnad (SEK)" : "Labor cost (SEK)"}</Label>
                    <Input 
                      type="number"
                      value={formData.estimated_cost}
                      onChange={(e) => setFormData({...formData, estimated_cost: parseFloat(e.target.value) || 0})}
                      data-testid="co-cost-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="form-label">{language === "sv" ? "Material (SEK)" : "Material (SEK)"}</Label>
                    <Input 
                      type="number"
                      value={formData.material_cost}
                      onChange={(e) => setFormData({...formData, material_cost: parseFloat(e.target.value) || 0})}
                      data-testid="co-material-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="form-label">{language === "sv" ? "UE (SEK)" : "Subcontractor (SEK)"}</Label>
                    <Input 
                      type="number"
                      value={formData.subcontractor_cost}
                      onChange={(e) => setFormData({...formData, subcontractor_cost: parseFloat(e.target.value) || 0})}
                      data-testid="co-sub-input"
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
                    {language === "sv" ? "Avbryt" : "Cancel"}
                  </Button>
                  <Button type="submit" className="flex-1" data-testid="co-submit-btn">
                    {language === "sv" ? "Skapa" : "Create"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                    {pendingCount}
                  </p>
                  <p className="text-sm text-slate-600">{language === "sv" ? "Väntande" : "Pending"}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FileText className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                    {totalValue.toLocaleString('sv-SE')}
                  </p>
                  <p className="text-sm text-slate-600">SEK</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder={language === "sv" ? "Sök ÄTA..." : "Search change orders..."}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-12"
              data-testid="co-search-input"
            />
          </div>
          <Select value={selectedProject || "all"} onValueChange={(v) => setSelectedProject(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full sm:w-[200px] h-12" data-testid="co-filter-project">
              <SelectValue placeholder={language === "sv" ? "Alla projekt" : "All projects"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === "sv" ? "Alla projekt" : "All projects"}</SelectItem>
              {projects.map(p => (
                <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Change Orders List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredCOs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">
                {search 
                  ? (language === "sv" ? "Inga ÄTA hittades" : "No change orders found")
                  : (language === "sv" ? "Inga ÄTA ännu" : "No change orders yet")}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredCOs.map((co) => {
              const status = statusConfig[co.status] || statusConfig.draft;
              const project = projects.find(p => p.project_id === co.project_id);
              const total = co.estimated_cost + co.material_cost + co.subcontractor_cost;
              
              return (
                <Card key={co.change_order_id} className="hover:shadow-md transition-shadow" data-testid={`co-card-${co.change_order_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900 truncate" style={{ fontFamily: 'Barlow Condensed' }}>
                            {co.title}
                          </h3>
                          <Badge className={status.color}>
                            {status.label}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500">{project?.name}</p>
                        <p className="text-sm text-slate-600 mt-2 line-clamp-2">{co.description}</p>
                        <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
                          {co.estimated_hours > 0 && (
                            <span className="text-slate-600">{co.estimated_hours}h</span>
                          )}
                          <span className="font-medium text-slate-900">
                            {total.toLocaleString('sv-SE')} SEK
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        {co.status === "draft" && (
                          <Button 
                            size="sm" 
                            onClick={() => updateStatus(co.change_order_id, "submitted")}
                            data-testid={`co-submit-${co.change_order_id}`}
                          >
                            {language === "sv" ? "Skicka in" : "Submit"}
                            <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        )}
                        {co.status === "submitted" && (
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={() => updateStatus(co.change_order_id, "under_review")}
                          >
                            {language === "sv" ? "Granska" : "Review"}
                          </Button>
                        )}
                        {co.status === "under_review" && (
                          <div className="flex gap-2">
                            <Button 
                              size="sm"
                              variant="outline"
                              className="text-green-600 border-green-200 hover:bg-green-50"
                              onClick={() => updateStatus(co.change_order_id, "approved")}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => updateStatus(co.change_order_id, "rejected")}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
};
